// v49.28 — Sidebar badge counts.
// Fetches unread/pending counts per sidebar destination so the
// Sidebar can render small notification pills next to each nav item.
// Subscribes to the same realtime/event channels the inbox + bell
// listen on, so counts stay in sync without polling.
//
// v50.68 — also tracks a `tenders` action count so partners and
// coordinators get a constant visual cue from any page that the bid
// engine has work waiting on them. Subscribes to bid_packs for the
// same realtime cadence as v50.58 (partner) / v50.59 (originator).

import { useCallback, useEffect, useState } from "react";
import { LABOURCORE_API } from "@/lib/api";
import { supabase } from "@/lib/api/supabase-client";
import { useShellUser } from "@/features/auth/AuthContext";

export interface SidebarBadges {
  inbox:         number; /* unread messages + pending contract offers */
  notifications: number; /* unread placement_notifications */
  /* v50.68 — bid engine action queue.
     Partner side: own packs in interest_registered or draft (you owe a draft / submission).
     Coordinator side: submitted packs on opps you originated (you owe a review).
     Hybrid: sum of both. */
  tenders:       number;
  /* v50.109 — futures action queue.
     Partner side: pending bookings sent to you (need accept / decline).
     Coordinator side: open partner_offers received against your projects (need accept / decline).
     Hybrid: sum of both. */
  futures:       number;
}

export function useSidebarBadges(): SidebarBadges {
  const user = useShellUser();
  const [badges, setBadges] = useState<SidebarBadges>({ inbox: 0, notifications: 0, tenders: 0, futures: 0 });

  const refresh = useCallback(async () => {
    if (!user?.id) { setBadges({ inbox: 0, notifications: 0, tenders: 0, futures: 0 }); return; }
    try {
      const [unreadMsgs, pendingOffers, unreadNotifs, tenderActions, futuresActions] = await Promise.all([
        supabase
          .from("placement_messages")
          .select("id", { count: "exact", head: true })
          .neq("sender_user_id", user.id)
          .is("read_by_other_at", null),
        supabase
          .from("contract_offers")
          .select("id", { count: "exact", head: true })
          .in("status", ["pending", "sent"]),
        LABOURCORE_API.placement_notifications.unreadCount(),
        loadTenderActionCount(user.role, user.companyId),
        loadFuturesActionCount(user.role, user.companyId),
      ]);
      setBadges({
        inbox:         (unreadMsgs.count || 0) + (pendingOffers.count || 0),
        notifications: unreadNotifs,
        tenders:       tenderActions,
        futures:       futuresActions,
      });
    } catch (e) {
      console.warn("[useSidebarBadges]", e);
    }
  }, [user?.id, user?.role, user?.companyId]);

  useEffect(() => { void refresh(); }, [refresh]);

  /* Live updates from every relevant feed. */
  useEffect(() => {
    if (!user?.id) return;
    const msgSub    = LABOURCORE_API.placement_messages.subscribe(() => { void refresh(); });
    const notifSub  = LABOURCORE_API.placement_notifications.subscribe(() => { void refresh(); });
    const onContract = () => { void refresh(); };
    window.addEventListener("lc-contract-offers-changed", onContract);

    /* v50.68 — bid_packs realtime so the /tenders badge tracks pack
       status flips. Filter-free since both partner-side (own packs)
       and originator-side (packs on own opps) need updates, and the
       JS-side counter re-filters in loadTenderActionCount. 1200ms
       implicit debounce by relying on refresh's atomic Promise.all. */
    const tenderChannel = supabase
      .channel(`sidebar-badges-tenders:${Math.random().toString(36).slice(2, 8)}`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .on("postgres_changes" as any, {
        event:  "*",
        schema: "public",
        table:  "bid_packs",
      }, () => { void refresh(); })
      .subscribe();

    /* v50.109 — futures_bookings + partner_offers status flips drive
       the /futures badge. Same filter-free shape as the tenders
       channel; loadFuturesActionCount re-filters server-side. */
    const futuresChannel = supabase
      .channel(`sidebar-badges-futures:${Math.random().toString(36).slice(2, 8)}`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .on("postgres_changes" as any, { event: "*", schema: "public", table: "futures_bookings" }, () => { void refresh(); })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .on("postgres_changes" as any, { event: "*", schema: "public", table: "partner_offers"   }, () => { void refresh(); })
      .subscribe();

    return () => {
      try { msgSub.unsubscribe?.(); }  catch { /* ignore */ }
      try { notifSub.unsubscribe?.(); } catch { /* ignore */ }
      window.removeEventListener("lc-contract-offers-changed", onContract);
      try { void supabase.removeChannel(tenderChannel); } catch { /* ignore */ }
      try { void supabase.removeChannel(futuresChannel); } catch { /* ignore */ }
    };
  }, [user?.id, refresh]);

  return badges;
}

/* ──────────────────────────────────────────────────────────────────── */

/* v50.68 — separate query so the role/company logic doesn't bloat the
   refresh callback. Returns 0 on any error or when there's no company
   to scope the query to. */
async function loadTenderActionCount(role: string | undefined, companyId: string | null | undefined): Promise<number> {
  if (!companyId) return 0;
  try {
    const queries: PromiseLike<{ count: number | null }>[] = [];

    /* Partner / hybrid: own packs needing your input. Matches the
       MyBidsInFlightCard "needs work" definition (v50.19/v50.58). */
    if (role === "partner" || role === "hybrid") {
      queries.push(
        supabase
          .from("bid_packs")
          .select("id", { count: "exact", head: true })
          .eq("partner_company_id", companyId)
          .in("status", ["interest_registered", "draft"])
          .then(({ count }) => ({ count })),
      );
    }

    /* Coordinator / hybrid: bids on own open opps awaiting an outcome
       decision. Counts both `submitted` (originator needs to shortlist
       or skip) and `shortlisted` (originator needs to mark won/lost).
       Matches BidsAttentionCard's submitted+shortlisted count
       (v50.18/v50.59).
       v50.69 — extended to include shortlisted; v50.68 only counted
       submitted, leaving stale won/lost decisions invisible in the
       sidebar.
       Two-step because bid_packs has no originator column. */
    if (role === "coordinator" || role === "hybrid") {
      queries.push(
        (async () => {
          const { data: opps } = await supabase
            .from("bid_opportunities")
            .select("id")
            .eq("originated_by_company_id", companyId)
            .eq("status", "open")
            .is("deleted_at", null);
          const oppIds = (opps as { id: string }[] | null)?.map(o => o.id) || [];
          if (oppIds.length === 0) return { count: 0 };
          const { count } = await supabase
            .from("bid_packs")
            .select("id", { count: "exact", head: true })
            .in("bid_opportunity_id", oppIds)
            .in("status", ["submitted", "shortlisted"]);
          return { count };
        })(),
      );
    }

    const results = await Promise.all(queries);
    return results.reduce((acc, r) => acc + (r.count || 0), 0);
  } catch (e) {
    console.warn("[useSidebarBadges] tenders count:", e);
    return 0;
  }
}

/* v50.109 — count futures items awaiting the viewer's decision.
   Partner side: pending bookings sent to me (accept / decline).
   Coordinator side: open partner_offers received against my projects. */
async function loadFuturesActionCount(role: string | undefined, companyId: string | null | undefined): Promise<number> {
  if (!companyId) return 0;
  try {
    const queries: PromiseLike<{ count: number | null }>[] = [];

    if (role === "partner" || role === "hybrid") {
      queries.push(
        supabase
          .from("futures_bookings")
          .select("id", { count: "exact", head: true })
          .eq("partner_company_id", companyId)
          .eq("status", "pending")
          .is("deleted_at", null)
          .then(({ count }) => ({ count })),
      );
    }

    if (role === "coordinator" || role === "hybrid") {
      queries.push(
        supabase
          .from("partner_offers")
          .select("id", { count: "exact", head: true })
          .eq("coordinator_company_id", companyId)
          .eq("status", "open")
          .is("deleted_at", null)
          .then(({ count }) => ({ count })),
      );
    }

    const results = await Promise.all(queries);
    return results.reduce((acc, r) => acc + (r.count || 0), 0);
  } catch (e) {
    console.warn("[useSidebarBadges] futures count:", e);
    return 0;
  }
}
